import { Article } from '@/types/article';
import apiClient from '@/utils/api';

export interface ArticleStatsEntry {
  code: string;
  nameEN: string;
  nameVI: string;
  createdCount: number;
  publishedCount: number;
  totalViews: number;
}

export interface CountryArticleStatsEntry extends ArticleStatsEntry {
  categories: ArticleStatsEntry[];
}

export interface ArticleStatsSummary {
  totalCreated: number;
  totalPublished: number;
  totalViews: number;
}

export interface ArticleStatsResponse {
  summary: ArticleStatsSummary;
  countries: CountryArticleStatsEntry[];
  topics: ArticleStatsEntry[];
}

export interface TopViewedArticleEntry {
  articleId: string;
  title: string;
  category: string;
  viewCount: number;
  publishedAt: string;
}

export interface ArticlePlanItem {
  articleId: string;
  createdAt: string;
  createdDate: string;
  createdBy: string;
  groupedBy?: 'topic' | 'country';
  countryCode?: string;
  topicCode?: string;
  categoryCode?: string;
  articleType?: string;
  articleTopic?: string;
  description?: string;
  tags?: string[];
  title?: string;
  titleUS?: string;
  publishedAt?: string;
}

export interface ArticlePlansResult {
  data: ArticlePlanItem[];
  cursor?: string;
}

const articleAdminBaseUrl = '/admin/articles';

type VoiceGender = 'male' | 'female';

type RegenerateTtsOptions = {
  voiceGender?: VoiceGender;
  dialogueVoiceGenders?: {
    A?: VoiceGender;
    B?: VoiceGender;
  };
};

export const articleAdminApi = {
  getAll: async (params?: {
    category?: string;
    status?: string;
    limit?: number;
    orderBy?: string;
    order?: 'asc' | 'desc';
    country?: string;
    cursor?: string;
    isSummaryReviewPage?: boolean;
    onlyPendingSummaryReview?: boolean;
    onlyPendingExtractedWordsReview?: boolean;
    onlyPendingTranslationReview?: boolean;
    onlyPendingTtsReview?: boolean;
    onlyPendingPublishReview?: boolean;
  }) => {
    let sendParams = { ...params };

    const response = await apiClient.get(`${articleAdminBaseUrl}`, { params: sendParams });
    return response.data;
  },
  getStats: async (): Promise<ArticleStatsResponse> => {
    const response = await apiClient.get(`${articleAdminBaseUrl}/stats`);
    return response.data.data;
  },
  getTopViewedArticles: async (params: {
    groupType: 'category' | 'topic';
    code: string;
    country?: string;
    limit?: number;
  }): Promise<TopViewedArticleEntry[]> => {
    const response = await apiClient.get(`${articleAdminBaseUrl}/stats/top-views`, { params });
    return response.data.data;
  },
  getPlans: async (params: {
    createdDate?: string;
    createdBy?: string;
    limit?: number;
    cursor?: string;
    order?: 'asc' | 'desc';
  }): Promise<ArticlePlansResult> => {
    const response = await apiClient.get(`${articleAdminBaseUrl}/plans`, { params });
    return {
      data: response.data.data,
      cursor: response.data.cursor,
    };
  },
  getById: async (country: string, id: string, options?: { includeVocabulary?: boolean }) => {
    let url = `${articleAdminBaseUrl}/${id}`;
    if (country) {
      url = `${articleAdminBaseUrl}/${id}/${country}`;
    }
    const response = await apiClient.get(url, {
      params: {
        includeVocabulary: options?.includeVocabulary ? 'true' : undefined,
      },
    });
    return response.data;
  },
  create: async (articleData: Record<string, unknown>) => {
    const response = await apiClient.post(`${articleAdminBaseUrl}`, articleData);
    return response.data;
  },
  update: async (country: string, id: string, articleData: Partial<Article>) => {
    let url = `${articleAdminBaseUrl}/${id}`;
    if (country) {
      url += `/${country}`;
    }
    const response = await apiClient.put(url, articleData);
    return response.data;
  },
  delete: async (country: string, id: string) => {
    let url = `${articleAdminBaseUrl}/${id}`;
    if (country) {
      url += `/${country}`;
    }
    const response = await apiClient.delete(url);
    return response.data;
  },
  publish: async (country: string, id: string) => {
    let url = `${articleAdminBaseUrl}/publish/${id}`;
    if (country) {
      url += `/${country}`;
    }
    const response = await apiClient.post(url);
    return response.data;
  },
  approveSummary: async (country: string, id: string, articleData: Article) => {
    let url = `${articleAdminBaseUrl}/summary/approve/${id}`;
    if (country) {
      url += `/${country}`;
    }
    const response = await apiClient.post(url, articleData);
    return response.data;
  },
  regenerateTts: async (
    country: string,
    id: string,
    level: 'A' | 'B' | 'C',
    options?: RegenerateTtsOptions
  ) => {
    let url = `${articleAdminBaseUrl}/tts/regenerate/${id}/${level}`;
    if (country) {
      url += `/${country}`;
    }
    const response = await apiClient.post(url, options || {});
    return response.data;
  },
  regenerateArticleOnlyTts: async (
    country: string,
    id: string,
    level: 'A' | 'B' | 'C',
    options?: RegenerateTtsOptions
  ) => {
    let url = `${articleAdminBaseUrl}/tts/regenerate-article/${id}/${level}`;
    if (country) {
      url += `/${country}`;
    }
    const response = await apiClient.post(url, options || {});
    return response.data;
  },
  regenerateLevelSentenceTts: async (
    country: string,
    id: string,
    level: 'A' | 'B' | 'C',
    options?: RegenerateTtsOptions
  ) => {
    let url = `${articleAdminBaseUrl}/tts/regenerate-sentences/${id}/${level}`;
    if (country) {
      url += `/${country}`;
    }
    const response = await apiClient.post(url, options || {});
    return response.data;
  },
  regenerateIpa: async (country: string, id: string, level: 'A' | 'B' | 'C') => {
    let url = `${articleAdminBaseUrl}/ipa/regenerate/${id}/${level}`;
    if (country) {
      url += `/${country}`;
    }
    const response = await apiClient.post(url);
    return response.data;
  },
  regenerateImage: async (country: string, id: string) => {
    let url = `${articleAdminBaseUrl}/image/regenerate/${id}`;
    if (country) {
      url += `/${country}`;
    }
    const response = await apiClient.post(url);
    return response.data;
  },
  publishArticle: async (
    country: string,
    id: string,
    categoryCode: string,
    publishedAt: string
  ) => {
    let url = `${articleAdminBaseUrl}/publish/${id}`;
    if (country) {
      url += `/${country}`;
    }
    const response = await apiClient.post(url, { categoryCode, publishedAt });

    return response.data;
  },
};
